// Deciding a suggestion (2026-10-18): the one place the screens approve or send back a row, so the job's Suggestions
// tab, the Library's queue and the Scope tab do it the same way. Approve as shown applies exactly the value given (the
// suggested one, or Spencer's edit of it); Send back returns the row with his note. Both are the database's functions;
// the screens only refresh what an approval may have changed.

import { isCatalogSuggestionKind } from '@zerogap/engine';
import { refresh, rpc, toast, type Row } from './api';

/** Every table an approval may change, refreshed after one: the row itself, the job, the library, a scope row. */
const TOUCHED = ['suggestion', 'job_item', 'sub_line', 'job_standard', 'add_on', 'job_route_line', 'job_bom_line', 'job_cost_line', 'recipe_route_step', 'recipe_bom_line', 'catalog_part', 'catalog_input', 'catalog_derived', 'catalog_child', 'operation', 'time_standard', 'recipe', 'catalog_item', 'material', 'vendor', 'scope_row', 'job', 'build', 'package', 'room', 'room_standard', 'mark', 'job_step'];

/** The value as the box holds it, typed the way the suggestion typed its own value. */
export function boxValue(s: Row, typed: string | undefined): unknown {
  if (typed === undefined) return s.new_value;
  return typeof s.new_value === 'number' && typed.trim() !== '' && Number.isFinite(Number(typed)) ? Number(typed) : typed;
}

/** Approve as shown. Returns the database's answer, or throws with its words (the caller shows them). */
export async function approveSuggestion(s: Row, value: unknown, note: string | null): Promise<unknown> {
  const out = await rpc('approve_suggestion', { p_id: s.id, p_value: value, p_note: note });
  refresh(...TOUCHED);
  return out;
}

/** Send back with a note; a blank note is refused here, before the database. */
export async function sendBackSuggestion(s: Row, note: string): Promise<void> {
  if (!note.trim()) throw new Error('Type a note first: it goes back with the suggestion.');
  await rpc('send_back_suggestion', { p_id: s.id, p_note: note.trim() });
  refresh('suggestion');
}

/** The words a decision shows, by kind. */
export function approvedWords(s: Row): string {
  if (s.kind === 'create_add_on') return 'Approved: the add-on is on the Builds tab, unpublished. Publishing it is yours.';
  if (String(s.kind).startsWith('scope_')) return 'Approved: the scope row reads as shown.';
  if (isCatalogKind(s.kind)) {
    const change = (s.payload as { change?: string } | null)?.change;
    // a removal retires the row since 2026-10-23: behind Show retired in the editor, with Restore
    if (change === 'remove') return 'Approved: the row is retired (behind Show retired, with Restore); the recipe is unverified until you verify it.';
    if (change === 'restore') return 'Approved: the row is back on the item.';
    return 'Approved as shown; the recipe is unverified until you verify it on the item.';
  }
  if (s.kind === 'retire_item') return 'Approved: the item is retired, out of every price and behind Show retired, with Restore.';
  if (s.kind === 'library_material' || s.kind === 'library_item') return 'Approved as shown; the row is unverified until you verify it.';
  if (s.kind === 'create_job') return 'Approved: the job is on the opportunity, with its base build.';
  if (s.kind === 'build_create' || s.kind === 'package_create') return 'Approved: it is on the Scope tab, unpublished. Publishing it is yours.';
  return 'Approved as shown.';
}

/** Say what went wrong, in the database's words. */
export const sayError = (e: unknown) => toast(e instanceof Error ? e.message : String(e));

/** The five kinds that change a catalog item's own rows (2026-10-18): they live in the Library's queue. */
export const isCatalogKind = (kind: unknown) => isCatalogSuggestionKind(kind);
/** The words for what a catalog kind changes, and for its table's main column. */
export const CATALOG_KIND_WORDS: Record<string, string> = { library_route_step: 'Route step', library_bom_line: 'Material line', library_part: 'Part', library_input: 'Input', library_calculation: 'Calculation' };
export const COLUMN_WORDS: Record<string, string> = {
  operation_id: 'operation', qty_expr: 'quantity formula', charge_setup: 'charges setup', note: 'note', material_id: 'material', material_role: 'material role', label: 'label',
  unit_price_expr: 'price formula', sub_expr: 'sub work when', link_expr: 'link formula', name: 'name', length_expr: 'length formula',
  width_expr: 'width formula', grain: 'grain', band_long_expr: 'long edges banded', band_short_expr: 'short edges banded', band_material_expr: 'banding material', grain_locked: 'grain locked', separate_pricing: 'separate pricing', markup_pct: 'markup %', ignore_margin: 'ignore margin',
  key: 'key', unit: 'unit', data_type: 'type', default_value: 'default', choices: 'choices', rule_expr: 'rule when', rule_message: 'rule message', info: 'info', material_filter: 'material filter',
  standard_key: 'follows the standard', hidden_expr: 'hidden when', expr: 'formula',
};

/** The four scope-row kinds (2026-10-18): they live on the Scope tab, not the Suggestions tab. */
export const SCOPE_KINDS = ['scope_reword', 'scope_link', 'scope_add', 'scope_dismiss', 'scope_source', 'scope_outcome', 'scope_tag'] as const;
export const isScopeKind = (kind: unknown) => (SCOPE_KINDS as readonly string[]).includes(String(kind));

// ---------- AI edit rights (docs/ai-edit-rights-brief.md, 2026-10-22) ----------

/** Everything a verification or a send-back may have changed, refreshed after one. */
const VERIFIED = ['job_item', 'scope_row', 'job', 'catalog_item', 'recipe', 'operation', 'material', 'job_type', 'suggestion', 'changes_since_verified', 'verification_changes', ...TOUCHED];

/** One Verify for a row (a job item, a scope row, the job's setup, a catalog item with its recipes, a library row), by a person. */
export async function verifyRow(table: string, id: string): Promise<void> {
  await rpc('verify_row', { p_table: table, p_id: id });
  refresh(...VERIFIED);
}

/** Send a row back to the version last verified, with a note the editor reads; a blank note is refused here, before the database. */
export async function sendBackRow(table: string, id: string, note: string): Promise<unknown> {
  if (!note.trim()) throw new Error('Type a note first: it goes to the editor with the send-back.');
  const out = await rpc('send_back', { p_table: table, p_id: id, p_note: note.trim() });
  refresh(...VERIFIED);
  return out;
}

/** Does a suggestion row touch this row (the same reading as ug.suggestion_touches): by its target, or by the row it made. */
export function suggestionTouches(x: Row, table: string, id: string, recipeIds: string[] = []): boolean {
  const p = (x.payload ?? {}) as Record<string, unknown>;
  switch (table) {
    case 'job_item':
      return (x.target_table === 'job_item' && x.target_id === id) || p.job_item_id === id || (x.kind === 'custom_line' && x.target_id === id);
    case 'scope_row':
      return isScopeKind(x.kind) && (x.target_id === id || p.scope_row_id === id);
    case 'job':
      return x.job_id === id && ['set_job_standard', 'job_route_step', 'job_bom_line', 'job_cost_line', 'room_add', 'room_rename', 'build_create', 'build_edit', 'package_create', 'package_edit', 'add_on_edit', 'edit_job', 'room_standard', 'job_pricing'].includes(String(x.kind));
    case 'catalog_item':
      return String(x.kind).startsWith('library_') && (p.catalog_item_id === id || x.target_id === id || recipeIds.includes(String(x.target_id)) || recipeIds.includes(String(p.recipe_id)));
    case 'recipe':
      return String(x.kind).startsWith('library_') && (p.recipe_id === id || x.target_id === id);
    case 'operation':
      return (x.kind === 'library_operation' || x.kind === 'library_time_standard') && x.target_id === id;
    case 'material':
      return x.kind === 'library_material' && x.target_id === id;
    default:
      return false;
  }
}
